import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IconDeviceFloppy, IconLanguage } from "@tabler/icons-react";
import { useFormik } from "formik";
import "quill/dist/quill.snow.css";
import "@assets/css/tour/add.css";
import "@assets/css/mode_tool/panel.css";
import "@assets/css/mode_tool/translatePointsModal.css";
import InlineError from "@components/forms/InlineError";
import AppModal from "@components/modals/AppModal";
import TourAddMultimedia from "@components/tour/addMultimedia";
import AiToolsPanel from "@components/ai_tools/AiToolsPanel";
import ThinkingAnimation from "@utils/ui/ThinkingAnimation";
import CountryCitySelector from "@components/locations/CountryCitySelector";
import {
  ensureTourLanguages,
  resetTourDraft,
  setTourIsActive,
  setTourInclusiveAccessType,
  setTourButtonTitleValue,
  setTourBadgeTitleValue,
  setTourDurationValue,
  setTourDescriptionValue,
  setTourCountryValue,
  setTourRegionValue,
  setTourCityValue,
  setTourLocation,
  setTourStatus,
  setTourNameValue,
  setTourPointCount,
  setTourStopCount,
} from "@store/slices/tourDraftSlice";
import { createTourRequest, updateTourRequest } from "@services/tours/toursApi";
import { TOUR_STATUS_OPTIONS } from "@utils/tours/tourStatus";
import {
  createQuillEditor,
  destroyQuillEditor,
  getQuillHtml,
} from "@utils/quill/editor";
import {
  buildCreateField,
  toPreviewText,
} from "@utils/tours/tourFormHelpers";
import { buildLanguageList } from "@utils/tours/tourPresentation";
import { buildTourSavePayload } from "@utils/tours/tourPayload";
import { buildTourValidationSchema } from "@utils/tours/tourValidation";
import { translateGenericStream } from "@services/ai/aiApi";

// Main create/update tour form rendered inside map tools panel.
function TourAddForm({
  languages,
  activeLanguageIso,
  onLocationPreview,
  onClearLocationPreview,
  onDone,
}) {
  const dispatch = useDispatch();
  const tourDraft = useSelector((state) => state.tourDraft);
  const {
    countryId,
    tourId,
    selectedLanguages,
    cityId,
    buttonTitle: buttonTitleByLang,
    badgeTitle: badgeTitleByLang,
    name: nameByLang,
    description: descriptionByLang,
    duration: durationByLang,
    country: countryByLang,
    region: regionByLang,
    city: cityByLang,
    pointCount,
    stopCount,
    status,
    isActive,
    inclusiveAccessType,
    multimedia,
    banner,
    snapshot,
  } = tourDraft;
  const languageList = useMemo(() => buildLanguageList(languages), [languages]);
  const [editor, setEditor] = useState({
    isOpen: false,
    field: "description",
    languageIso: "",
    value: "",
  });
  const [quillHost, setQuillHost] = useState(null);
  const quillInstanceRef = useRef(null);
  const quillHandlerRef = useRef(null);
  const aiToolsAnchorRef = useRef(null);
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const [isDescriptionEditorReady, setIsDescriptionEditorReady] =
    useState(false);
  const [savingTour, setSavingTour] = useState(false);
  const [isTourTranslateOpen, setIsTourTranslateOpen] = useState(false);
  const [tourTranslateLanguage, setTourTranslateLanguage] = useState("");
  const [isTourTranslating, setIsTourTranslating] = useState(false);
  const [tourTranslateError, setTourTranslateError] = useState("");
  const [tourTranslateComplete, setTourTranslateComplete] = useState(false);
  const activeIsos = useMemo(() => {
    const normalizedSelected = Array.from(
      new Set(
        (Array.isArray(selectedLanguages) ? selectedLanguages : [])
          .map((item) => String(item?.iso || "").toLowerCase().trim())
          .filter(Boolean),
      ),
    );
    const baseList = normalizedSelected.length
      ? languageList.filter((language) => normalizedSelected.includes(language.iso))
      : languageList;
    return baseList.map((language) => language.iso).filter(Boolean);
  }, [languageList, selectedLanguages]);
  const requiredIsos = useMemo(() => {
    return activeIsos.includes("en") ? ["en"] : [];
  }, [activeIsos]);
  const currentLanguageIso = useMemo(() => {
    if (activeIsos.includes(activeLanguageIso)) return activeLanguageIso;
    if (activeIsos.includes("en")) return "en";
    return activeIsos[0] || "";
  }, [activeIsos, activeLanguageIso]);
  const isEnglishLanguage = String(currentLanguageIso).toLowerCase() === "en";
  const translationLanguages = useMemo(
    () => languageList.filter((language) => language.iso !== "en"),
    [languageList],
  );
  const currentLanguageLabel = useMemo(() => {
    const currentLanguage = languageList.find(
      (language) => language.iso === currentLanguageIso,
    );
    return currentLanguage?.name || currentLanguageIso.toUpperCase();
  }, [currentLanguageIso, languageList]);
  const tourImagesCount = useMemo(
    () =>
      (multimedia || []).filter((item) => {
        const mediaType = String(item?.mediaType || "").toLowerCase();
        const mimeType = String(item?.mimeType || "").toLowerCase();
        return mediaType === "image" || mimeType.startsWith("image/");
      }).length,
    [multimedia],
  );
  const promoVideoCount = useMemo(() => {
    const videoIsos = new Set(
      (multimedia || [])
        .filter((item) => {
          const mediaType = String(item?.mediaType || "").toLowerCase();
          const mimeType = String(item?.mimeType || "").toLowerCase();
          return (
            (mediaType === "video" || mimeType.startsWith("video/")) &&
            item?.mediaRole === "promoVideo" &&
            item?.languageIso
          );
        })
        .map((item) => item.languageIso),
    );
    return videoIsos.size;
  }, [multimedia]);
  const promoAudioCount = useMemo(() => {
    const audioIsos = new Set(
      (multimedia || [])
        .filter((item) => {
          const mediaType = String(item?.mediaType || "").toLowerCase();
          const mimeType = String(item?.mimeType || "").toLowerCase();
          return (
            (mediaType === "audio" || mimeType.startsWith("audio/")) &&
            item?.mediaRole === "promoAudio" &&
            item?.languageIso
          );
        })
        .map((item) => item.languageIso),
    );
    return audioIsos.size;
  }, [multimedia]);
  const promoAudioTitlesValid = useMemo(() => {
    const promoAudios = (multimedia || []).filter((item) => {
      const mediaType = String(item?.mediaType || "").toLowerCase();
      const mimeType = String(item?.mimeType || "").toLowerCase();
      return (
        (mediaType === "audio" || mimeType.startsWith("audio/")) &&
        item?.mediaRole === "promoAudio"
      );
    });
    if (!requiredIsos.length) return false;
    return requiredIsos.every((iso) => {
      const items = promoAudios.filter((audio) => audio?.languageIso === iso);
      if (!items.length) return false;
      return items.every(
        (item) => String(item?.audioTitles?.[iso] || "").trim().length > 0,
      );
    });
  }, [multimedia, requiredIsos]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      countryId,
      cityId,
      status,
      isActive,
      inclusiveAccessType,
      bannerId: banner?.id || "",
      snapshotId: snapshot?.id || "",
      tourImages: (multimedia || [])
        .filter((item) => {
          const mediaType = String(item?.mediaType || "").toLowerCase();
          const mimeType = String(item?.mimeType || "").toLowerCase();
          return mediaType === "image" || mimeType.startsWith("image/");
        })
        .map((item) => item.id)
        .filter((id) => Boolean(id) && id !== banner?.id),
      tourImagesCount,
      promoVideoCount,
      promoAudioCount,
      promoAudioTitlesValid,
      buttonTitle: buildCreateField(buttonTitleByLang, activeIsos),
      badgeTitle: buildCreateField(badgeTitleByLang, activeIsos),
      name: buildCreateField(nameByLang, activeIsos),
      description: buildCreateField(descriptionByLang, activeIsos),
      duration: buildCreateField(durationByLang, activeIsos),
      country: buildCreateField(countryByLang, activeIsos),
      region: buildCreateField(regionByLang, activeIsos),
      city: buildCreateField(cityByLang, activeIsos),
      pointCount,
      stopCount,
    },
    validationSchema: buildTourValidationSchema(activeIsos),
    onSubmit: () => {},
  });

  function touchAllRequiredFields() {
    const touchedByIso = activeIsos.reduce(
      (acc, iso) => {
        if (iso !== requiredIsos[0]) return acc;
        acc.name[iso] = true;
        acc.buttonTitle[iso] = true;
        acc.badgeTitle[iso] = true;
        acc.description[iso] = true;
        acc.duration[iso] = true;
        acc.country[iso] = true;
        acc.region[iso] = true;
        acc.city[iso] = true;
        return acc;
      },
      {
        name: {},
        buttonTitle: {},
        badgeTitle: {},
        description: {},
        duration: {},
        country: {},
        region: {},
        city: {},
      },
    );

    return formik.setTouched({
      countryId: true,
      cityId: true,
      status: true,
      isActive: true,
      bannerId: true,
      snapshotId: true,
      tourImagesCount: true,
      promoVideoCount: true,
      promoAudioCount: true,
      promoAudioTitlesValid: true,
      buttonTitle: touchedByIso.buttonTitle,
      badgeTitle: touchedByIso.badgeTitle,
      pointCount: true,
      stopCount: true,
      name: touchedByIso.name,
      description: touchedByIso.description,
      duration: touchedByIso.duration,
      country: touchedByIso.country,
      region: touchedByIso.region,
      city: touchedByIso.city,
    }, true);
  }

  useEffect(() => {
    const isos = languageList.map((language) => language.iso);
    dispatch(ensureTourLanguages(isos));
  }, [dispatch, languageList]);

  function pickLocalizedFieldError(fieldName) {
    const requiredIso = requiredIsos[0];
    if (!requiredIso) return "";
    const touchedField = formik.touched?.[fieldName];
    const fieldErrors = formik.errors?.[fieldName];
    if (!touchedField || typeof touchedField !== "object") return "";
    return String(fieldErrors?.[requiredIso] || "");
  }

  // Opens per-language modal editor for description.
  function openEditor(field, languageIso) {
    const source = descriptionByLang;
    const value = source[languageIso] || "";
    setEditor({
      isOpen: true,
      field,
      languageIso,
      value,
    });
    if (field === "description") {
      setDescriptionHtml(value);
    }
  }

  // Closes modal editor and resets temporary editor state.
  function closeEditor() {
    setEditor({
      isOpen: false,
      field: "description",
      languageIso: "",
      value: "",
    });
    setIsDescriptionEditorReady(false);
  }

  // Persists current modal editor value into Redux draft.
  function saveEditor() {
    dispatch(
      setTourDescriptionValue({
        iso: editor.languageIso,
        value: descriptionHtml,
      }),
    );
    formik.setFieldValue(
      `description.${editor.languageIso}`,
      toPreviewText(descriptionHtml, 1000),
      true,
    );
    formik.setFieldTouched(`description.${editor.languageIso}`, true, true);
    closeEditor();
  }

  function openTourTranslate() {
    setTourTranslateLanguage("");
    setTourTranslateError("");
    setTourTranslateComplete(false);
    setIsTourTranslateOpen(true);
  }

  async function translateTour() {
    if (!tourId || !tourTranslateLanguage) return;
    const source = {
      id: String(tourId),
      name: String(nameByLang.en || ""),
      description: String(descriptionByLang.en || ""),
      buttonTitle: String(buttonTitleByLang.en || ""),
      badgeTitle: String(badgeTitleByLang.en || ""),
    };
    setIsTourTranslating(true);
    setTourTranslateError("");
    setTourTranslateComplete(false);
    try {
      let pointError = "";
      const response = await translateGenericStream(
        { language: tourTranslateLanguage, data: [source] },
        ({ event, data }) => {
          if (
            event === "point" &&
            String(data?.id || "") === String(tourId) &&
            data?.status === "error"
          ) {
            pointError = data?.message || "The Tour could not be translated.";
          }
        },
      );
      const translated = response?.body?.data?.find(
        (item) => String(item?.id || "") === String(tourId),
      );
      if (response?.error || pointError || !translated) {
        throw new Error(pointError || response?.message || "The translation response did not include this Tour.");
      }
      const iso = tourTranslateLanguage;
      const translatedName = String(translated.name ?? source.name);
      const translatedDescription = String(translated.description ?? source.description);
      const translatedButtonTitle = String(translated.buttonTitle ?? source.buttonTitle);
      const translatedBadgeTitle = String(translated.badgeTitle ?? source.badgeTitle);
      dispatch(setTourNameValue({ iso, value: translatedName }));
      dispatch(setTourDescriptionValue({ iso, value: translatedDescription }));
      dispatch(setTourButtonTitleValue({ iso, value: translatedButtonTitle }));
      dispatch(setTourBadgeTitleValue({ iso, value: translatedBadgeTitle }));
      formik.setFieldValue(`name.${iso}`, translatedName, false);
      formik.setFieldValue(`description.${iso}`, toPreviewText(translatedDescription, 1000), false);
      formik.setFieldValue(`buttonTitle.${iso}`, translatedButtonTitle, false);
      formik.setFieldValue(`badgeTitle.${iso}`, translatedBadgeTitle, false);
      setTourTranslateComplete(true);
    } catch (error) {
      setTourTranslateError(error?.message || "Could not translate this Tour.");
    } finally {
      setIsTourTranslating(false);
    }
  }

  useEffect(() => {
    // Lazy-initializes rich text editor only for description modal.
    if (!editor.isOpen || editor.field !== "description") return;
    if (!quillHost || quillInstanceRef.current) return;

    const quill = createQuillEditor({
      container: quillHost,
      initialHtml: descriptionHtml,
    });
    if (!quill) return;

    quillHandlerRef.current = () => setDescriptionHtml(getQuillHtml(quill));
    quill.on("text-change", quillHandlerRef.current);
    quillInstanceRef.current = quill;
    setIsDescriptionEditorReady(true);
  }, [descriptionHtml, editor.field, editor.isOpen, quillHost]);

  useEffect(() => {
    // Destroys rich text editor when modal closes to avoid memory leaks.
    if (!editor.isOpen && quillInstanceRef.current) {
      destroyQuillEditor({
        container: quillHost,
        quill: quillInstanceRef.current,
        textChangeHandler: quillHandlerRef.current,
      });
      quillInstanceRef.current = null;
      quillHandlerRef.current = null;
      setIsDescriptionEditorReady(false);
      setQuillHost(null);
    }
  }, [editor.isOpen, quillHost]);

  useEffect(
    () => () => {
      // Safety cleanup if component unmounts while editor exists.
      if (!quillInstanceRef.current) return;
      destroyQuillEditor({
        container: quillHost,
        quill: quillInstanceRef.current,
        textChangeHandler: quillHandlerRef.current,
      });
      quillInstanceRef.current = null;
      quillHandlerRef.current = null;
    },
    [quillHost],
  );

  // Builds final payload and sends create/update request.
  async function handleSubmitTour() {
    const payload = buildTourSavePayload(tourDraft, activeIsos);

    try {
      const errors = await formik.validateForm();
      if (Object.keys(errors || {}).length) {
        console.log("createTour validation errors:", errors);
        await touchAllRequiredFields();
        return;
      }
    } catch {
      return;
    }

    // Required log to validate the payload sent to the backend.
    console.log("createTour payload:", payload);

    setSavingTour(true);
    try {
      if (tourId) {
        await updateTourRequest(tourId, payload);
      } else {
        await createTourRequest(payload);
      }
      dispatch(resetTourDraft());
      if (typeof onClearLocationPreview === "function")
        onClearLocationPreview();
      if (typeof onDone === "function") onDone();
    } catch {
    } finally {
      setSavingTour(false);
    }
  }

  return (
    <>
      <div className="tour-add-shell" ref={aiToolsAnchorRef}>
        <div className="tour-add-form map-tools-form-placeholder">
        {/* Name block: localized values edited per selected language. */}
        <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            name
            {pickLocalizedFieldError("name") ? (
              <InlineError>{pickLocalizedFieldError("name")}</InlineError>
            ) : null}
          </p>
          <label className="field-label app-form-field">
            <input
              className="field-input"
              value={nameByLang[currentLanguageIso] || ""}
              placeholder={`Write name (${currentLanguageIso})`}
              onChange={(event) => {
                dispatch(
                  setTourNameValue({
                    iso: currentLanguageIso,
                    value: event.target.value,
                  }),
                );
                formik.setFieldValue(
                  `name.${currentLanguageIso}`,
                  event.target.value,
                  true,
                );
              }}
              onBlur={() => {
                formik.setFieldTouched(
                  `name.${currentLanguageIso}`,
                  true,
                  true,
                );
              }}
            />
          </label>
        </div>

        {/* Description block: localized rich text edited per selected language. */}
        <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            description
            {pickLocalizedFieldError("description") ? (
              <InlineError>{pickLocalizedFieldError("description")}</InlineError>
            ) : null}
          </p>
          <button
            type="button"
            className={`field-label app-form-field field-preview-row ${descriptionByLang[currentLanguageIso] ? "filled" : ""}`}
            onClick={() => openEditor("description", currentLanguageIso)}
          >
            {descriptionByLang[currentLanguageIso] ? (
              <span
                className="field-preview-value field-preview-value-html"
                dangerouslySetInnerHTML={{
                  __html: String(descriptionByLang[currentLanguageIso] || ""),
                }}
              />
            ) : (
              <span className="field-preview-value">Complete</span>
            )}
          </button>
        </div>

        {/* Media block: main image + tour images + promo media + snapshot. */}
        <TourAddMultimedia
          languages={languageList}
          activeLanguageIso={currentLanguageIso}
          showPrimaryTourMedia={isEnglishLanguage}
          bannerValidationError={
            formik.touched.bannerId ? formik.errors.bannerId : ""
          }
          snapshotValidationError={
            formik.touched.snapshotId ? formik.errors.snapshotId : ""
          }
          tourImagesValidationError={
            formik.touched.tourImagesCount ? formik.errors.tourImagesCount : ""
          }
          promoVideoValidationError={
            formik.touched.promoVideoCount ? formik.errors.promoVideoCount : ""
          }
          promoAudioValidationError={
            formik.touched.promoAudioCount ? formik.errors.promoAudioCount : ""
          }
          promoAudioTitlesValidationError={
            formik.touched.promoAudioTitlesValid
              ? formik.errors.promoAudioTitlesValid
              : ""
          }
        />

        {/* Location block: country + city selection synchronized with main map. */}
        {isEnglishLanguage ? <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            location
            {formik.touched.countryId && formik.errors.countryId ? (
              <InlineError>{formik.errors.countryId}</InlineError>
            ) : null}
            {formik.touched.cityId && formik.errors.cityId ? (
              <InlineError>{formik.errors.cityId}</InlineError>
            ) : null}
          </p>
          <CountryCitySelector
            value={{ countryId, cityId }}
            countryError={
              formik.touched.countryId ? formik.errors.countryId : ""
            }
            cityError={formik.touched.cityId ? formik.errors.cityId : ""}
            onChange={(payload) => {
              dispatch(setTourLocation(payload));
              formik.setFieldValue("countryId", payload?.countryId || "", true);
              formik.setFieldValue("cityId", payload?.cityId || "", true);
              formik.setFieldTouched("countryId", true, true);
              formik.setFieldTouched("cityId", true, true);
            }}
            onLocationPreview={onLocationPreview}
            onClearLocationPreview={onClearLocationPreview}
          />
        </div> : null}

        {/* Localized country: manual value per selected language. */}
        {isEnglishLanguage ? <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            country
            {pickLocalizedFieldError("country") ? (
              <InlineError>{pickLocalizedFieldError("country")}</InlineError>
            ) : null}
          </p>
          <label className="field-label app-form-field">
            <input
              className="field-input"
              value={countryByLang[currentLanguageIso] || ""}
              placeholder={`Write country (${currentLanguageIso})`}
              onChange={(event) => {
                dispatch(
                  setTourCountryValue({
                    iso: currentLanguageIso,
                    value: event.target.value,
                  }),
                );
                formik.setFieldValue(
                  `country.${currentLanguageIso}`,
                  event.target.value,
                  true,
                );
              }}
              onBlur={() =>
                formik.setFieldTouched(
                  `country.${currentLanguageIso}`,
                  true,
                  true,
                )
              }
            />
          </label>
        </div> : null}

        {/* Localized region: manual value per selected language. */}
        {isEnglishLanguage ? <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            region
            {pickLocalizedFieldError("region") ? (
              <InlineError>{pickLocalizedFieldError("region")}</InlineError>
            ) : null}
          </p>
          <label className="field-label app-form-field">
            <input
              className="field-input"
              value={regionByLang[currentLanguageIso] || ""}
              placeholder={`Write region (${currentLanguageIso})`}
              onChange={(event) => {
                dispatch(
                  setTourRegionValue({
                    iso: currentLanguageIso,
                    value: event.target.value,
                  }),
                );
                formik.setFieldValue(
                  `region.${currentLanguageIso}`,
                  event.target.value,
                  true,
                );
              }}
              onBlur={() =>
                formik.setFieldTouched(
                  `region.${currentLanguageIso}`,
                  true,
                  true,
                )
              }
            />
          </label>
        </div> : null}

        {/* Localized city: manual value per selected language. */}
        {isEnglishLanguage ? <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            city
            {pickLocalizedFieldError("city") ? (
              <InlineError>{pickLocalizedFieldError("city")}</InlineError>
            ) : null}
          </p>
          <label className="field-label app-form-field">
            <input
              className="field-input"
              value={cityByLang[currentLanguageIso] || ""}
              placeholder={`Write city (${currentLanguageIso})`}
              onChange={(event) => {
                dispatch(
                  setTourCityValue({
                    iso: currentLanguageIso,
                    value: event.target.value,
                  }),
                );
                formik.setFieldValue(
                  `city.${currentLanguageIso}`,
                  event.target.value,
                  true,
                );
              }}
              onBlur={() =>
                formik.setFieldTouched(`city.${currentLanguageIso}`, true, true)
              }
            />
          </label>
        </div> : null}

        {/* Lifecycle block: business status and active flag required by CMS rules. */}
        {isEnglishLanguage ? <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            status and active
            {formik.touched.status && formik.errors.status ? (
              <InlineError>{formik.errors.status}</InlineError>
            ) : null}
            {formik.touched.isActive && formik.errors.isActive ? (
              <InlineError>{formik.errors.isActive}</InlineError>
            ) : null}
          </p>
          <div className="tour-add-form-grid">
            <label className="field-label app-form-field">
              <span className="label-inline">status</span>
              <select
                className="field-input select-control"
                value={status}
                onChange={(event) => {
                  dispatch(setTourStatus(Number(event.target.value)));
                  formik.setFieldValue(
                    "status",
                    Number(event.target.value),
                    true,
                  );
                  formik.setFieldTouched("status", true, true);
                }}
              >
                {TOUR_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.value} - {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field-label app-form-field">
              <span className="label-inline">isActive</span>
              <select
                className="field-input select-control"
                value={isActive ? "true" : "false"}
                onChange={(event) => {
                  dispatch(setTourIsActive(event.target.value === "true"));
                  formik.setFieldValue(
                    "isActive",
                    event.target.value === "true",
                    true,
                  );
                  formik.setFieldTouched("isActive", true, true);
                }}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </label>
          </div>
        </div> : null}

        {/* Duration block: localized values edited per selected language. */}
        {isEnglishLanguage ? <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            tour duration
            {pickLocalizedFieldError("duration") ? (
              <InlineError>{pickLocalizedFieldError("duration")}</InlineError>
            ) : null}
          </p>
          <label className="field-label app-form-field">
            <input
              className="field-input"
              value={durationByLang[currentLanguageIso] || ""}
              placeholder={`Write duration (${currentLanguageIso})`}
              onChange={(event) => {
                dispatch(
                  setTourDurationValue({
                    iso: currentLanguageIso,
                    value: event.target.value,
                  }),
                );
                formik.setFieldValue(
                  `duration.${currentLanguageIso}`,
                  event.target.value,
                  true,
                );
              }}
              onBlur={() => {
                formik.setFieldTouched(
                  `duration.${currentLanguageIso}`,
                  true,
                  true,
                );
              }}
            />
          </label>
        </div> : null}

        {isEnglishLanguage ? <div className="tour-add-form-group">
          <p className="tour-add-form-label">
            points and stops
            {formik.touched.pointCount && formik.errors.pointCount ? (
              <InlineError>{formik.errors.pointCount}</InlineError>
            ) : null}
            {formik.touched.stopCount && formik.errors.stopCount ? (
              <InlineError>{formik.errors.stopCount}</InlineError>
            ) : null}
          </p>
          <div className="tour-add-form-grid">
            <label className="field-label app-form-field">
              <span className="label-inline">point count</span>
              <input
                type="number"
                min="0"
                step="1"
                className="field-input"
                value={pointCount}
                onChange={(event) => {
                  const nextValue = Math.max(
                    0,
                    Number.parseInt(event.target.value || "0", 10) || 0,
                  );
                  dispatch(setTourPointCount(nextValue));
                  formik.setFieldValue("pointCount", nextValue, true);
                }}
                onBlur={() => formik.setFieldTouched("pointCount", true, true)}
              />
            </label>
            <label className="field-label app-form-field">
              <span className="label-inline">stop count</span>
              <input
                type="number"
                min="0"
                step="1"
                className="field-input"
                value={stopCount}
                onChange={(event) => {
                  const nextValue = Math.max(
                    0,
                    Number.parseInt(event.target.value || "0", 10) || 0,
                  );
                  dispatch(setTourStopCount(nextValue));
                  formik.setFieldValue("stopCount", nextValue, true);
                }}
                onBlur={() => formik.setFieldTouched("stopCount", true, true)}
              />
            </label>
          </div>
        </div> : null}

        <div className="tour-add-form-group tour-add-form-group-block">
          <p className="tour-add-form-label">labels and badges</p>
          <div className="tour-add-form-grid">
            <label className="field-label app-form-field">
              <span className="label-inline">
                button title
                {pickLocalizedFieldError("buttonTitle") ? (
                  <InlineError>{pickLocalizedFieldError("buttonTitle")}</InlineError>
                ) : null}
              </span>
              <input
                className="field-input"
                value={buttonTitleByLang[currentLanguageIso] || ""}
                placeholder={`Write button title (${currentLanguageIso})`}
                onChange={(event) => {
                  dispatch(
                    setTourButtonTitleValue({
                      iso: currentLanguageIso,
                      value: event.target.value,
                    }),
                  );
                  formik.setFieldValue(
                    `buttonTitle.${currentLanguageIso}`,
                    event.target.value,
                    true,
                  );
                }}
                onBlur={() => {
                  formik.setFieldTouched(
                    `buttonTitle.${currentLanguageIso}`,
                    true,
                    true,
                  );
                }}
              />
            </label>

            <label className="field-label app-form-field">
              <span className="label-inline">
                badge title
                {pickLocalizedFieldError("badgeTitle") ? (
                  <InlineError>{pickLocalizedFieldError("badgeTitle")}</InlineError>
                ) : null}
              </span>
              <input
                className="field-input"
                value={badgeTitleByLang[currentLanguageIso] || ""}
                placeholder={`Write badge title (${currentLanguageIso})`}
                onChange={(event) => {
                  dispatch(
                    setTourBadgeTitleValue({
                      iso: currentLanguageIso,
                      value: event.target.value,
                    }),
                  );
                  formik.setFieldValue(
                    `badgeTitle.${currentLanguageIso}`,
                    event.target.value,
                    true,
                  );
                }}
                onBlur={() => {
                  formik.setFieldTouched(
                    `badgeTitle.${currentLanguageIso}`,
                    true,
                    true,
                  );
                }}
              />
            </label>
          </div>
        </div>

        {isEnglishLanguage ? <div className="tour-add-form-group">
          <label className="field-label app-form-field">
            <span className="label-inline">InclussiveAccessType</span>
            <input
              type="checkbox"
              checked={Boolean(inclusiveAccessType)}
              onChange={(event) => {
                const nextValue = Boolean(event.target.checked);
                dispatch(setTourInclusiveAccessType(nextValue));
                formik.setFieldValue("inclusiveAccessType", nextValue, true);
              }}
            />
          </label>
        </div> : null}

        <AppModal
          isOpen={editor.isOpen}
          onClose={closeEditor}
          title={`${editor.field} (${editor.languageIso})`}
          size="lg"
        >
          {/* Modal editor used for each translated field. */}
          <div className="tour-add-editor">
            {!isDescriptionEditorReady ? (
              <p className="tour-add-editor-loading">Loading editor...</p>
            ) : null}
            <div ref={setQuillHost} className="tour-add-quill" />
            <div className="tour-add-editor-actions">
              <button
                type="button"
                className="button-primary"
                onClick={saveEditor}
              >
                Save
              </button>
              <button
                type="button"
                className="button-ghost"
                onClick={closeEditor}
              >
                Cancel
              </button>
            </div>
          </div>
        </AppModal>
        <AppModal
          isOpen={isTourTranslateOpen}
          onClose={() => setIsTourTranslateOpen(false)}
          closeDisabled={isTourTranslating}
          title="AI Translate Tour"
          size="sm"
          className="mode-route-translate-modal"
        >
          <p>Translate the Tour name, description, button title, and badge title from English.</p>
          <fieldset className="mode-route-translate-languages" disabled={isTourTranslating || tourTranslateComplete}>
            <legend>Target language</legend>
            {translationLanguages.map((language) => (
              <label key={language.iso}>
                <input
                  type="radio"
                  name="tour-translation-language"
                  value={language.iso}
                  checked={tourTranslateLanguage === language.iso}
                  onChange={() => setTourTranslateLanguage(language.iso)}
                />
                <span>{language.name || language.iso.toUpperCase()}</span>
                <small>{language.iso.toUpperCase()}</small>
              </label>
            ))}
          </fieldset>
          {!tourId ? <p className="message-error">Save the Tour before translating it.</p> : null}
          {!translationLanguages.length ? <p>No translation languages are available for this Tour.</p> : null}
          {isTourTranslating ? <ThinkingAnimation label="Translating Tour..." /> : null}
          {tourTranslateComplete ? <p role="status">Tour translated. Review the selected language before saving.</p> : null}
          {tourTranslateError ? <p className="message-error" role="alert">{tourTranslateError}</p> : null}
          <div className="tour-add-editor-actions">
            <button type="button" className="button-ghost" onClick={() => setIsTourTranslateOpen(false)} disabled={isTourTranslating}>Cancel</button>
            {!tourTranslateComplete ? (
              <button
                type="button"
                className="button-primary"
                onClick={translateTour}
                disabled={!tourId || !tourTranslateLanguage || isTourTranslating || !translationLanguages.length}
              >
                {isTourTranslating ? "Translating..." : "Translate"}
              </button>
            ) : null}
          </div>
        </AppModal>
        </div>
      </div>
      <div className="mode-route-tool-steps tour-add-save-row">
        <button
          type="button"
          className={`app-icon-btn app-icon-btn--add mode-route-tool-route-btn ${savingTour ? "is-disabled" : ""} ${savingTour ? "is-loading" : ""}`}
          onClick={handleSubmitTour}
          disabled={savingTour}
          aria-disabled={savingTour}
          aria-label={tourId ? "Update tour" : "Save tour"}
          title={tourId ? "Update tour" : "Save tour"}
        >
          {savingTour ? (
            <span className="mode-route-tool-btn-spinner" />
          ) : (
            <>
              Save
              <IconDeviceFloppy size={16} />
            </>
          )}
        </button>
      </div>
      <AiToolsPanel
        anchorRef={aiToolsAnchorRef}
        title="AI TOOLS"
        ariaLabel="Tour AI TOOLS"
        isVisible={isEnglishLanguage}
        actions={[
          {
            id: "translate-tour",
            label: "AI Translate",
            icon: <IconLanguage size={17} />,
            onClick: openTourTranslate,
          },
        ]}
      />
    </>
  );
}

export default TourAddForm;
